import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCake,
  MdOutlineChangeCircle,
  MdOutlineDateRange,
  MdOutlineHealthAndSafety,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import {
  CASE_TYPES,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  PROJECT_TYPES,
  SALE_MODES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Contract = ({
  initialValues,
  disabled,
  setDisabled,
  setPercent,
  isFieldRequired,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const eSignDisabled =
    get(initialValues, "project.contract.saleMode") === SALE_MODES.DISTANT &&
    get(initialValues, "project.projectType") === PROJECT_TYPES.COLD_LEAD &&
    get(initialValues, "createdDate") &&
    moment().diff(get(initialValues, "createdDate"), "hours") <= 24;

  const SALES_MODES = {
    [SALE_MODES.FACE_TO_FACE]: (value) => {
      set(initialValues, "project.projectType", PROJECT_TYPES.HOT_LEAD);
      setDisabled((prev) => ({
        ...prev,
        project: {
          ...prev.project,
          projectType: [SALE_MODES.FACE_TO_FACE].includes(value),
        },
      }));
    },
    [SALE_MODES.DISTANT]: (_) => {
      set(initialValues, "project.projectType", null);
      setDisabled((prev) => ({
        ...prev,
        project: {
          ...prev.project,
          projectType: false,
        },
      }));
    },
  };

  const issuanceDatePath = "project.contract.issuanceDate";
  const issuanceDateField = bindField(issuanceDatePath, {
    label: SUBSCRIPTION.FORM.START_DATE.LABEL,
    required: isFieldRequired(issuanceDatePath),
    disabled: true,
    onBlur: () => setPercent(initialValues),
  });

  const caseTypePath = "project.contract.caseType";
  const caseTypeField = bindField(caseTypePath, {
    label: SUBSCRIPTION.FORM.CASE_TYPE.LABEL,
    required: isFieldRequired(caseTypePath),
    disabled: get(disabled, "project.contract.caseType"),
    onBlur: () => setPercent(initialValues),
  });

  const riaPath = "project.ria";
  const riaField = bindField(riaPath, {
    label: SUBSCRIPTION.FORM.RIA.LABEL,
    disabled: true,
  });

  const saleModePath = "project.contract.saleMode";
  const saleModeField = bindField(saleModePath, {
    label: SUBSCRIPTION.FORM.SALE_MODE.LABEL,
    required: isFieldRequired(saleModePath),
    disabled: get(disabled, "project.contract.saleMode"),
    onChange: (value) => {
      SALES_MODES[value](value);
      setPercent(initialValues);
    },
  });

  const projectTypePath = "project.projectType";
  const projectTypeField = bindField(projectTypePath, {
    label: SUBSCRIPTION.FORM.PROJECT_TYPE.LABEL,
    required: isFieldRequired(projectTypePath),
    disabled: get(disabled, "project.projectType"),
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const createdDatePath = "createdDate";
  const createdDateField = bindField(createdDatePath, {
    label: SUBSCRIPTION.FORM.QUOTE_RECEIVED_DATE.LABEL,
    required: isFieldRequired(createdDatePath),
    disabled: true,
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTRACT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTRACT.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={issuanceDateField.path}
              label={issuanceDateField.label}
              required={issuanceDateField.required}
              disabled={issuanceDateField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.START_DATE.PLACEHOLDER}
                  type="date"
                  variant="simple"
                  label={issuanceDateField.label}
                  icon={<MdOutlineCake size={16} />}
                  defaultValue={
                    get(initialValues, issuanceDatePath) &&
                    dayjs(get(initialValues, issuanceDatePath))
                  }
                  {...field}
                  onChange={issuanceDateField.onChange}
                  onBlur={issuanceDateField.onBlur}
                  format={DATE_FORMAT}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={caseTypeField.path}
              label={caseTypeField.label}
              required={caseTypeField.required}
              disabled={caseTypeField.disabled}
            >
              {(field) => (
                <Select
                  type="simple"
                  label={caseTypeField.label}
                  icon={<MdOutlineHealthAndSafety size={16} />}
                  placeholder={SUBSCRIPTION.FORM.CASE_TYPE.PLACEHOLDER}
                  options={Object.entries(CASE_TYPES).map(([key, value]) => ({
                    label: SUBSCRIPTION.FORM.CASE_TYPE.OPTIONS[key],
                    value,
                  }))}
                  defaultValue={caseTypeField.defaultValue}
                  {...field}
                  onChange={caseTypeField.onChange}
                  onBlur={caseTypeField.onBlur}
                  optional={!caseTypeField.required}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={riaField.path}
              label={riaField.label}
              required={riaField.required}
              disabled={riaField.disabled}
            >
              {(field) => (
                <Input
                  type="switch"
                  label={riaField.label}
                  icon={<MdOutlineChangeCircle size={16} />}
                  defaultChecked={get(initialValues, riaPath)}
                  {...field}
                  onChange={riaField.onChange}
                  onBlur={riaField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={saleModeField.path}
              label={saleModeField.label}
              required={saleModeField.required}
              disabled={saleModeField.disabled}
            >
              {(field) => (
                <Select
                  type="simple"
                  label={saleModeField.label}
                  icon={<MdOutlineHealthAndSafety size={16} />}
                  placeholder={SUBSCRIPTION.FORM.SALE_MODE.PLACEHOLDER}
                  options={Object.entries(SALE_MODES).map(([key, value]) => ({
                    label: SUBSCRIPTION.FORM.SALE_MODE.OPTIONS[key],
                    value,
                  }))}
                  defaultValue={saleModeField.defaultValue}
                  {...field}
                  onChange={saleModeField.onChange}
                  onBlur={saleModeField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={projectTypeField.path}
              label={projectTypeField.label}
              required={projectTypeField.required}
              disabled={projectTypeField.disabled}
            >
              {(field) => (
                <Select
                  type="simple"
                  label={projectTypeField.label}
                  icon={<MdOutlineHealthAndSafety size={16} />}
                  placeholder={SUBSCRIPTION.FORM.PROJECT_TYPE.PLACEHOLDER}
                  options={Object.entries(PROJECT_TYPES).map(
                    ([key, value]) => ({
                      label: SUBSCRIPTION.FORM.PROJECT_TYPE.OPTIONS[key],
                      value,
                    }),
                  )}
                  value={get(initialValues, projectTypePath)}
                  {...field}
                  onChange={(value) => {
                    if (value) {
                      projectTypeField.onChange(value);
                    } else {
                      setPercent(initialValues);
                    }
                  }}
                  onBlur={projectTypeField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={createdDateField.path}
              label={createdDateField.label}
              required={createdDateField.required}
              disabled={createdDateField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={
                    SUBSCRIPTION.FORM.QUOTE_RECEIVED_DATE.PLACEHOLDER
                  }
                  type="date"
                  variant="simple"
                  label={createdDateField.label}
                  icon={<MdOutlineDateRange size={16} />}
                  defaultValue={
                    get(initialValues, createdDatePath) &&
                    dayjs(get(initialValues, createdDatePath))
                  }
                  {...field}
                  onChange={createdDateField.onChange}
                  onBlur={createdDateField.onBlur}
                  format={DATE_TIME_FORMAT}
                  optional={!createdDateField.required}
                />
              )}
            </FormField>
            {eSignDisabled && get(initialValues, "createdDate") && (
              <span className="warning-message">
                {format(SUBSCRIPTION.ADDITIONAL_INFORMATION.E_SIGN, {
                  time: moment(get(initialValues, "createdDate"))
                    .add(24, "hours")
                    .format(DATE_TIME_FORMAT),
                })}
              </span>
            )}
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Contract;
